import CodeIcon from '@mui/icons-material/Code';
import {Fab, styled} from '@mui/material';
import {func, number} from 'prop-types';
import React, {memo, useCallback, useRef, useState} from 'react';
import ReactDOM from 'react-dom';

const StyledFab = styled(Fab)(({theme}) => ({
  border: `2px solid ${theme.palette.grey[200]}`,
}));

const StyledSliderDiv = styled('div')(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  height: '100%',
  position: 'absolute',
  top: 0,
  left: '50%',
  zIndex: 0,
  transform: 'translateX(-50%)',
  '&:before,&:after': {
    content: '""',
    display: 'block',
    flexGrow: 1,
    width: 3,
    boxShadow: theme.shadows[1],
    backgroundColor: theme.palette.grey[100],
    position: 'relative',
    zIndex: 1,
  },
}));

/**
 * The slider that controls comparing two maps layer of different time periods.
 * It is based upon src/components/compare/compare-slider.js from Explorer but
 * has been simplified and generalized for this specific use case.
 */
const CompareSlider = ({mapWidth, initialValue, onChange}) => {
  const containerRef = useRef();

  const [fraction, setFraction] = useState(initialValue / 100);

  const onDrag = useCallback(
    event => {
      const clientX = event.clientX;
      const node = ReactDOM.findDOMNode(containerRef.current);
      const rect = node.parentNode.getBoundingClientRect();
      const position = (clientX - rect.left) / rect.width;

      const nextFraction = Math.max(0, Math.min(1, position));
      setFraction(nextFraction);
      onChange(nextFraction * 100);
    },
    [onChange],
  );

  const onDragEnd = useCallback(() => {
    document.removeEventListener('mousemove', onDrag);
    document.removeEventListener('mouseup', onDragEnd);
  }, [onDrag]);

  const onDragStart = useCallback(() => {
    document.addEventListener('mousemove', onDrag);
    document.addEventListener('mouseup', onDragEnd);
  }, [onDrag, onDragEnd]);

  const getPixelFromPosition = useCallback(
    position => mapWidth * position,
    [mapWidth],
  );

  const handlePosition = getPixelFromPosition(fraction);

  return (
    <StyledSliderDiv ref={containerRef} style={{left: handlePosition}}>
      <StyledFab
        color="primary"
        size="small"
        disableRipple
        onMouseDown={onDragStart}
        data-cy="compare-tool-slider-handle"
      >
        <CodeIcon fontSize="small" />
      </StyledFab>
    </StyledSliderDiv>
  );
};

CompareSlider.propTypes = {
  initialValue: number.isRequired,
  mapWidth: number.isRequired,
  onChange: func.isRequired,
};

export default memo(CompareSlider);
